import { useContext, useEffect, useRef, useState } from "react";
import { useAuth0, IdToken } from "@auth0/auth0-react";
import { useMutation } from "react-query";
import { params } from "../../params";
import { useAuthorizedQuery } from "..";
import { FeatureAccessContext } from "../../context/featureAccess";
import { useTrace } from "@/context/trace";
import * as Sentry from "@sentry/react";

export type AutoResponseAgent =
  | { type: "unassigned" }
  | { type: "agent"; id: number };

export function useGetAutoResponseAgent(enabled: boolean = true) {
  return useAuthorizedQuery<AutoResponseAgent>({
    queryKey: `useGetAutoResponseAgent`,
    url: `${params.API_URL}/platform/auto-activate/auto-response-agent`,
    method: "GET",
    enabled,
  });
}

export type AutoResponseFallbackAgent =
  | { type: "unassigned" }
  | {
      type: "group";
      externalId: string;
    }
  | { type: "agent"; externalId: string; groupId: string };

export function useGetAutoResponseFallbackAgent(enabled: boolean = true) {
  return useAuthorizedQuery<AutoResponseFallbackAgent>({
    queryKey: `useGetAutoResponseFallbackAgent`,
    url: `${params.API_URL}/platform/auto-activate/auto-response-fallback-agent`,
    method: "GET",
    enabled,
  });
}

type UpdateAutoResponseConfigRequest = {
  autoResponseAgentId?: number | null;
  autoResponseFallbackAgent?: string | null;
};

export function useUpdateAutoResponseConfig() {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { createTraceId, getCurrentSpanId } = useTrace();

  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  return useMutation<number, unknown, UpdateAutoResponseConfigRequest>({
    mutationKey: `update_auto_response_config`,
    mutationFn: async (data: UpdateAutoResponseConfigRequest) => {
      return Sentry.startSpan(
        {
          name: `PUT /platform/auto-activate/tenant-auto-config`,
          op: "http.client",
        },
        async (span) => {
          try {
            abortControllerRef.current?.abort();
            const newAbortController = new AbortController();
            abortControllerRef.current = newAbortController;

            const spanContext = span.spanContext();
            const traceId = createTraceId();
            const spanId = spanContext.spanId || getCurrentSpanId();
            const sessionId = sessionStorage.getItem("sessionId") || "";

            const traceIdW3CFormat =
              `00-${traceId.padStart(32, "0").substring(0, 32)}` +
              `-${spanId?.padStart(16, "0").substring(0, 16)}-01`;

            const traceIdFromHeader = traceIdW3CFormat.split("-")[1];

            Sentry.setTag("apiTraceId", traceIdFromHeader);
            Sentry.setTag("sessionId", sessionId);
            Sentry.setContext("request", {
              url: `${params.API_URL}/platform/auto-activate/tenant-auto-config`,
              method: "PUT",
              traceId: traceIdFromHeader,
              spanId,
              sessionId,
            });

            const res = await fetch(
              `${params.API_URL}/platform/auto-activate/tenant-auto-config`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${tokenId?.__raw ?? ""}`,
                  "x-tenant-id": selectedWorkspace ?? "",
                  "x-trace-id": traceIdW3CFormat,
                  "x-session-id": sessionId,
                },
                body: JSON.stringify(data),
                signal: newAbortController.signal,
              }
            );

            const contentType = res.headers.get("content-type");
            if (contentType?.includes("application/json")) {
              return await res.json();
            } else {
              return await res.text();
            }
          } catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
              return;
            }
            const responseError = {
              type: "Error",
              message:
                (error instanceof Error && error.message) ||
                "Something went wrong",
            };

            const customError = new Error(responseError.message);
            Object.assign(customError, responseError);
            throw customError;
          }
        }
      );
    },
  });
}
